import { Link } from "react-router-dom";
import { Instagram, Linkedin, Youtube, Twitter } from "lucide-react";

const COLS = [
  {
    title: "Academy",
    links: [
      { label: "Home", to: "/" },
      { label: "Packages", to: "/#packages" },
      { label: "Courses", to: "/#courses" },
      { label: "Community", to: "/1-percent-community-club" },
      { label: "Contact", to: "/#enquire" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "FAQ", to: "/#faq" },
      { label: "Support", to: "mailto:support@skillpreneuracademy.com", external: true },
      { label: "Mentorship", to: "/super-profile-program" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms & Conditions", to: "/terms-and-conditions" },
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Refund & Cancellation Policy", to: "/refund-policy" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-slate-200/70 dark:border-white/10 pt-10 sm:pt-12 pb-8"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <a
              href="#top"
              data-testid="footer-brand-logo"
              className="inline-flex items-center gap-2.5"
            >
              <img
                src="/logo-icon-96.png"
                srcSet="/logo-icon-64.png 1x, /logo-icon-128.png 2x, /logo-icon-256.png 3x"
                alt="SkillPreneur Academy"
                width="40"
                height="40"
                decoding="async"
                className="h-10 w-10 shrink-0 aspect-square rounded-xl object-contain shadow-sm ring-1 ring-black/5 dark:ring-white/10"
              />
              <div className="flex flex-col leading-none">
                <span className="font-display font-semibold tracking-tight text-base text-[#0F172A] dark:text-white">
                  SkillPreneur
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Academy
                </span>
              </div>
            </a>
            <p className="mt-6 text-sm text-slate-600 dark:text-slate-300 max-w-sm leading-relaxed">
              The modern path to high-income skills. Built for students,
              professionals, freelancers and aspiring entrepreneurs.
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {COLS.map((c) => (
              <div key={c.title}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                  {c.title}
                </div>
                <ul className="mt-5 space-y-3">
                  {c.links.map((l) => {
                    const testid = `footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
                    if (l.external || l.to.startsWith("mailto:") || l.to.startsWith("http")) {
                      return (
                        <li key={l.label}>
                          <a
                            href={l.to}
                            data-testid={testid}
                            className="text-sm text-slate-700 dark:text-slate-300 hover:text-[#0F172A] dark:hover:text-white transition-colors"
                          >
                            {l.label}
                          </a>
                        </li>
                      );
                    }
                    if (l.to.includes("#")) {
                      return (
                        <li key={l.label}>
                          <a
                            href={l.to}
                            data-testid={testid}
                            className="text-sm text-slate-700 dark:text-slate-300 hover:text-[#0F172A] dark:hover:text-white transition-colors"
                          >
                            {l.label}
                          </a>
                        </li>
                      );
                    }
                    return (
                      <li key={l.label}>
                        <Link
                          to={l.to}
                          data-testid={testid}
                          className="text-sm text-slate-700 dark:text-slate-300 hover:text-[#0F172A] dark:hover:text-white transition-colors"
                        >
                          {l.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-slate-200/70 dark:border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            © {new Date().getFullYear()} SkillPreneur Academy. All rights
            reserved.
          </p>
          <div className="flex items-center gap-3">
            <Link
              to="/student/login"
              data-testid="footer-student-login"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 dark:text-slate-400 hover:text-[#0F172A] dark:hover:text-white hover:border-[#06B6D4]/40 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
            >
              Student Login
            </Link>
            <div className="flex items-center gap-2">
              {[Instagram, Linkedin, Youtube, Twitter].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  aria-label="social"
                  className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
